import { useState, useMemo } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Upload, Loader2, Check, X, Youtube, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


export default function BulkImportTab({ shows, seasons, onRefresh }) {
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [targetShowId, setTargetShowId] = useState("");
  const [targetSeasonId, setTargetSeasonId] = useState("");
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  // Get seasons filtered by show
  const filteredSeasons = useMemo(() => {
    if (!targetShowId) return [];
    return seasons.filter(s => s.show_id === targetShowId);
  }, [targetShowId, seasons]);

  const handleFetchPlaylist = async () => {
    if (!playlistUrl.trim()) {
      toast.error("Ingresa una URL de playlist");
      return;
    }

    setLoading(true);
    setPlaylistVideos([]);
    setSelectedVideos(new Set());

    try {
      const response = await adminApi.post(`/youtube/playlist`, {
        playlist_url: playlistUrl
      });

      if (response.data.videos && response.data.videos.length > 0) {
        setPlaylistVideos(response.data.videos);
        // Select all by default
        setSelectedVideos(new Set(response.data.videos.map((_, i) => i)));
        toast.success(`Se encontraron ${response.data.videos.length} videos`);
      } else {
        toast.error("No se encontraron videos en la playlist");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar la playlist");
    } finally {
      setLoading(false);
    }
  };

  const toggleVideo = (index) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedVideos(newSelected);
  };

  const selectAll = () => {
    setSelectedVideos(new Set(playlistVideos.map((_, i) => i)));
  };

  const deselectAll = () => {
    setSelectedVideos(new Set());
  };

  const handleImport = async () => {
    if (!targetSeasonId) {
      toast.error("Selecciona una temporada de destino");
      return;
    }

    if (selectedVideos.size === 0) {
      toast.error("Selecciona al menos un video");
      return;
    }

    setImporting(true);
    const videosToImport = playlistVideos.filter((_, i) => selectedVideos.has(i));
    setImportProgress({ current: 0, total: videosToImport.length });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < videosToImport.length; i++) {
      const video = videosToImport[i];
      try {
        await adminApi.post(`/videos`, {
          title: video.title,
          description: video.description || "",
          video_url: video.video_url,
          thumbnail: video.thumbnail,
          platform: "youtube",
          season_id: targetSeasonId,
          order: video.position || i
        });
        successCount++;
      } catch (error) {
        console.error(`Error importing video: ${video.title}`, error);
        errorCount++;
      }
      setImportProgress({ current: i + 1, total: videosToImport.length });
    }

    setImporting(false);

    if (successCount > 0) {
      toast.success(`Se importaron ${successCount} videos correctamente`);
      onRefresh();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} videos no se pudieron importar`);
    }

    // Reset
    setPlaylistVideos([]);
    setSelectedVideos(new Set());
    setPlaylistUrl("");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-bold mb-2 flex items-center gap-2 text-black">
          <Youtube size={24} className="text-red-500" />
          Importar desde YouTube Playlist
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          Pega la URL de una playlist de YouTube para importar todos sus videos de una vez.
        </p>

        {/* Playlist URL Input */}
        <div className="flex gap-3 mb-6">
          <Input
            value={playlistUrl}
            onChange={(e) => setPlaylistUrl(e.target.value)}
            placeholder="https://www.youtube.com/playlist?list=..."
            className="flex-1 bg-gray-50 border-gray-200 text-black"
            disabled={loading || importing}
            data-testid="playlist-url-input"
          />
          <Button 
            onClick={handleFetchPlaylist}
            disabled={loading || importing || !playlistUrl.trim()}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="fetch-playlist-btn"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Cargando...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Cargar Playlist
              </>
            )}
          </Button>
        </div>

        {/* Target Selection */}
        {playlistVideos.length > 0 && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Show destino *</label>
              <Select
                value={targetShowId || "none"}
                onValueChange={(value) => {
                  setTargetShowId(value === "none" ? "" : value);
                  setTargetSeasonId("");
                }}
                disabled={importing}
              >
                <SelectTrigger className="bg-white border-gray-200 text-black" data-testid="import-show-select">
                  <SelectValue placeholder="Seleccionar show" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleccionar show</SelectItem>
                  {shows.map((show) => (
                    <SelectItem key={show.id} value={show.id}>
                      {show.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Temporada destino *</label>
              <Select
                value={targetSeasonId || "none"}
                onValueChange={(value) => setTargetSeasonId(value === "none" ? "" : value)}
                disabled={!targetShowId || importing}
              >
                <SelectTrigger className="bg-white border-gray-200 text-black" data-testid="import-season-select">
                  <SelectValue placeholder={targetShowId ? "Seleccionar temporada" : "Elige show primero"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleccionar temporada</SelectItem>
                  {filteredSeasons.map((season) => (
                    <SelectItem key={season.id} value={season.id}>
                      {season.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Videos List */}
      {playlistVideos.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-black">
              Videos encontrados ({playlistVideos.length})
            </h3>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={selectAll}
                disabled={importing}
                className="text-xs"
              >
                Seleccionar todos
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={deselectAll}
                disabled={importing}
                className="text-xs"
              >
                Deseleccionar todos
              </Button>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            {selectedVideos.size} de {playlistVideos.length} seleccionados
          </p>

          {/* Progress bar during import */}
          {importing && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Importando...</span>
                <span>{importProgress.current} / {importProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Videos Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2">
            {playlistVideos.map((video, index) => (
              <div
                key={index}
                onClick={() => !importing && toggleVideo(index)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedVideos.has(index)
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                } ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`playlist-video-${index}`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  selectedVideos.has(index) ? 'bg-green-500 text-white' : 'bg-gray-200'
                }`}>
                  {selectedVideos.has(index) ? <Check size={14} /> : <span className="text-xs text-gray-500">{index + 1}</span>}
                </div>
                {video.thumbnail ? (
                  <img 
                    src={video.thumbnail} 
                    alt={video.title}
                    className="w-20 h-12 object-cover rounded flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                    <Film size={16} className="text-gray-400" />
                  </div>
                )}
                <p className="text-sm font-medium text-black line-clamp-2 flex-1">
                  {video.title}
                </p>
              </div>
            ))}
          </div>

          {/* Import Button */}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleImport}
              disabled={importing || selectedVideos.size === 0 || !targetSeasonId}
              className="bg-green-500 hover:bg-green-600 text-white px-8"
              data-testid="import-videos-btn"
            >
              {importing ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Importar {selectedVideos.size} videos
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
